import { useNavigate } from 'react-router-dom';
import type { ListData } from '../../../types';
import BlurFillImage from './BlurFillImage';

const ListIcon = ({ size = 10 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="2 4 20 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const ItemRow = ({
  data,
  noMargin,
  isAdmin,
}: {
  data: ListData;
  noMargin?: boolean;
  isAdmin?: boolean;
}) => {
  const navigate = useNavigate();

  // Use author if available, otherwise calculate domain from URL
  const origin =
    data.author ||
    (data.sourceUrl
      ? (() => {
          try {
            const url = new URL(data.sourceUrl);
            return url.hostname.replace('www.', '');
          } catch {
            return '';
          }
        })()
      : '');

  // Calculate time/progress message
  const timeMessage = (() => {
    const listened = data.secondsListened || 0;
    const total = data.secondsTotal ?? 0;
    if (data.status === 'pending') return 'Loading article\u2026';
    if (total <= 0) return null;
    const leftMin = Math.floor((total - listened) / 60);
    if (leftMin <= 0) return 'Done';
    const suffix = listened > 0 ? ' left' : '';
    if (leftMin >= 60) {
      const hrs = Math.floor(leftMin / 60);
      const mins = leftMin % 60;
      return mins > 0 ? `${hrs} hr ${mins} min${suffix}` : `${hrs} hr${suffix}`;
    }
    return `${leftMin} min${suffix}`;
  })();

  // Chapter count for playlists
  const chapterCount = (() => {
    if (data.type !== 'playlist' || !data.contentIds) return null;
    try {
      return JSON.parse(data.contentIds).length;
    } catch {
      return null;
    }
  })();

  // Build subtitle parts: origin · time · chapters
  const subtitleParts = [origin, timeMessage].filter(Boolean);

  // Admins can navigate to failed content to delete/retry
  const isFailed = data.status === 'failed';
  const canNavigate = !isFailed || isAdmin;

  return (
    <div
      style={{
        minHeight: '60px',
        padding: '8px 0',
        margin: noMargin ? 0 : '0 10px',
        display: 'flex',
        alignItems: 'center',
        cursor: canNavigate ? 'pointer' : 'default',
        opacity: canNavigate ? 1 : 0.7,
      }}
      onClick={() => {
        if (canNavigate) {
          navigate(`/id/${data.id}`);
        }
      }}
    >
      <BlurFillImage
        imageUrl={data.imageUrl}
        style={{
          width: '60px',
          height: '60px',
          flexShrink: 0,
        }}
      />
      <div
        style={{
          flex: 1,
          padding: '6px 10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <h3 className="item-title">{data.title}</h3>
        {data.status === 'failed' ? (
          <span
            style={{
              fontSize: '0.8rem',
              color: 'white',
              backgroundColor: '#dc3545',
              borderRadius: '12px',
              padding: '2px 8px',
              display: 'inline-block',
              width: 'fit-content',
            }}
          >
            Upload Failed
          </span>
        ) : (
          <div
            className="subtle-text"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0',
              flexWrap: 'wrap',
            }}
          >
            {subtitleParts.map((part, i) => (
              <span key={i} style={{ color: 'inherit', whiteSpace: 'nowrap' }}>
                {i > 0 && ' • '}
                {part}
              </span>
            ))}
            {chapterCount != null && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  whiteSpace: 'nowrap',
                  color: 'inherit',
                }}
              >
                {subtitleParts.length > 0 && <span>&nbsp;•&nbsp;</span>}
                <ListIcon size={12} />
                {chapterCount} chapter{chapterCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemRow;
